/**
 * Sidebar is a React component that renders a sidebar
 * with menu items based on user permissions and the current state
 * of the sidebar (collapsed or expanded).
 *
 * @param {SidebarProps} props - The props for the component.
 * @returns {JSX.Element} The rendered sidebar component.
 */
import React, { useState } from "react";
import {
  Menu,
  HomeIcon,
  AvatarIcon,
  HelpIcon,
  DescriptionIcon,
  Sidebar as ForgeCommonSidebar,
  Logo,
  Tooltip,
  ImportIcon,
} from "@forge/common";
import "./Sidebar.scss";
import { WrapperUserPermissions } from "../../../models";
import staticContent from "../../../utils/staticContent.json";

type MenuItems = "Home" | "AddNewOrg" | "Help" | "DownloadPDF";

interface SidebarProps {
  activeMenuItem: MenuItems;
  handleMenuClick: (menuItem: MenuItems) => void;
  onClose: () => void;
  userPermissions?: WrapperUserPermissions;
  isCollapsed?: boolean;
}

const Sidebar = ({
  activeMenuItem,
  handleMenuClick,
  onClose,
  userPermissions,
  isCollapsed,
}: SidebarProps) => {
  const [showHelpSubmenu, setShowHelpSubmenu] = useState(false);

  // Home menu item (only if permission)
  const homeMenuItem = userPermissions?.can_read_tenant && {
    label: isCollapsed ? "" : staticContent.home,
    prefix: (
      <Tooltip content={staticContent.home} position="right-top">
        <HomeIcon
          fontSize={24}
          data-testid="home"
          aria-current={activeMenuItem === "Home" ? "true" : undefined}
        />
      </Tooltip>
    ),
    path: "/home",
    onClick: () => handleMenuClick("Home"),
    isActiveItem: activeMenuItem === "Home",
    className: "home-icon",
  };

  // Add New Organisations menu item
  const addNewOrganizationsMenuItem = {
    label: isCollapsed ? "" : staticContent.addNewOrganisations,
    path: "/add-new-org",
    prefix: (
      <Tooltip content={staticContent.addNewOrganisations} position="right-top">
        <AvatarIcon fontSize={24} data-testid="addNewOrg" />
      </Tooltip>
    ),
    onClick: () => handleMenuClick("AddNewOrg"),
    isActiveItem: activeMenuItem === "AddNewOrg",
    className: "add-new-org-menu-item color-bg-menu-item-hover",
  };

// Help submenu items (collapsed state)
const helpSubmenuItemsCollapsed = [
  {
    label: "",
    path: "/online-help",
    prefix: (
      <Tooltip content={staticContent.onlineHelp} position="right-top">
        <DescriptionIcon fontSize={24} data-testid="onlineHelp" />
      </Tooltip>
    ),
    onClick: () => handleMenuClick("Help"),
    isActiveItem: activeMenuItem === "Help",
  },
  {
    label: "",
    path: "/download-pdf",
    prefix: (
      <Tooltip content={staticContent.downloadPdf} position="right-top">
        <ImportIcon fontSize={24} data-testid="downloadHelp" />
      </Tooltip>
    ),
    onClick: () => handleMenuClick("DownloadPDF"),
    isActiveItem: activeMenuItem === "DownloadPDF",
  },
];

// Help menu items (expanded state).
const helpMenuItemsExpanded = [
  {
    label: staticContent.onlineHelp,
    path: "/online-help",
    onClick: () => handleMenuClick("Help"),
    prefix: <HelpIcon />,
    isActiveItem: activeMenuItem === "Help",
  },
  {
    label: staticContent.downloadPdf,
    path: "/download-pdf",
    onClick: () => handleMenuClick("DownloadPDF"),
    prefix: <DescriptionIcon />,
    isActiveItem: activeMenuItem === "DownloadPDF",
  },
];

/**
 * 
 * Menu configuration for the Help section.
 * If the sidebar is collapsed, it shows a single Help icon
 * that toggles the submenu.
 * If the sidebar is expanded, it shows the full Help menu (
 * Online Help, Download PDF).
 * 
 */
const helpMenuConfig = isCollapsed
  ? {
      items: [
        {
          label: "",
          path: "/help",
          prefix: (
            <Tooltip content={staticContent.help} position="right-top">
              <HelpIcon fontSize={24} data-testid="help" />
            </Tooltip>
          ),
          onClick: () => setShowHelpSubmenu((prev) => !prev),
          className: "help-menu-item color-bg-menu-item-hover",
        },
        ...(showHelpSubmenu ? helpSubmenuItemsCollapsed : []),
      ],
      title: "",
    }
  : {
      items: helpMenuItemsExpanded,
      title: staticContent.help,
    };

  /**
   *
   * Menu configuration for the sidebar.
   * The first menu group contains the Home and Add New Organisations items,
   * while the second menu group contains the Help items.
   * Help items: Online Help and Download PDF.
   */
  const menuConfig = [
    {
      items: [homeMenuItem, addNewOrganizationsMenuItem].filter(
        (item): item is Exclude<typeof item, false | undefined> => Boolean(item)
      ),
      title: "", // Empty title for the first menu
    },
    helpMenuConfig,
  ];

  return (
    <ForgeCommonSidebar
      header={
        isCollapsed ? null : (
          <Logo
            orientation="main"
            suite={staticContent.suite}
            domain={staticContent.domain}
            suiteSuperScript={staticContent.suiteSuperScript}
          />
        )
      }
      className={`sidebar-container ${
        isCollapsed ? "collapsed" : "expanded"
      } menu-Bar theme-side-menu`}
      showClose={!isCollapsed}
      onClose={onClose}
      backdrop={!isCollapsed}
    >
      <div 
        className="sidebar-menu-container"
        key={isCollapsed ? "collapsed" : "expanded"}
      >
        {Menu({
          config: menuConfig
        })}
      </div>
    </ForgeCommonSidebar>
  );
};

export default React.memo(Sidebar);
